import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Building2, Copy, Plus, RefreshCw, Trash2 } from "lucide-react";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import SearchBar from "../components/SearchBar";
import SortableTable from "../components/SortableTable";
import PaginationControls from "../components/PaginationControls";
import ExportButton from "../components/ExportButton";
import Button from "../../../common/Button";
import CopyGroupModal from "./AccessGroupDetail/CopyGroupModal";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import {
  getEnabledResources,
  getFilterTagModifier,
  getVisibleResources,
} from "./accessGroupResources";
import { SELECTED_COMPANY_KEY } from "../../../../lib/local/SelectedCompany";
import "../../../common/SelectInput/style.css";
import "../style.css";
import "./RolesList.css";

export const SELECTED_COMPANY_STORAGE_KEY = SELECTED_COMPANY_KEY;

const ACCESS_CODE_LABELS = {
  "absence-code": "Absence Codes",
  absence_codes: "Absence Codes",
  emp: "Employees",
  equip: "Equipment",
  extype: "Expense Types",
  flog: "Field Logs",
  inv: "Inventory",
  jcct: "Cost Types",
  job: "Jobs",
  prclass: "Worker Classes",
  prdept: "Payroll Departments",
  ven: "Vendors",
  wloc: "Work Locations",
  subcontract: "Subcontracts",
  gl: "GL Accounts",
  prunion: "Payroll Unions",
  prwcomp: "Workers Comp",
  apitime: "Time",
  apiexpenses: "Expenses",
  svcwrko: "Work Orders",
  salesrep: "Sales Reps",
  customer: "Customers",
  svcsite: "Service Sites",
  svccontract: "Service Contracts",
  svcequip: "Service Equipment",
};

function getGroupTypeLabel(groupType) {
  return (groupType || "app") === "api" ? "API" : "Field";
}

export function getAccessCodeLabel(code) {
  const lastUnderscore = code.lastIndexOf("_");
  if (lastUnderscore === -1) return code;
  const prefix = code.substring(0, lastUnderscore);
  const suffix = code.substring(lastUnderscore + 1);
  const label = ACCESS_CODE_LABELS[prefix] || prefix;
  return suffix === "write" ? `${label} (Write)` : label;
}

// Company options for the "Company" picker — alphabetized, excluding
// companies where this user's access is inactive. Shared by AccessGroupsList
// (filter dropdown) and AccessGroupForm (company picker when adding a group).
// Pass `permissionData` (one of the per-permission maps on `companies`, e.g.
// companies.configureApprovalGroupsData) to further restrict the list to
// only companies where the user actually holds that specific permission —
// otherwise every company the user belongs to is offered.
export function getActiveCompanyOptions(companies, permissionData) {
  return Object.entries(companies?.data || {})
    .filter(([uuid]) => companies?.enabledData?.[uuid] !== false)
    .filter(([uuid]) => !permissionData || permissionData[uuid] !== undefined)
    .map(([uuid, name]) => {
      const code = companies?.codesMap?.[uuid];
      return {
        value: uuid,
        label: code != null ? `${name} (${code})` : name,
        name,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export default function AccessGroupsList({ onGroupSelect, onAddGroup }) {
  const { accessGroups, fetchGroups, deleteGroup, companies } = useContext(
    FieldConfigurationContext
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [paginationConfig, setPaginationConfig] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState("");
  const [copyGroup, setCopyGroup] = useState(null);
  const [groupFilters, setGroupFilters] = useState({});
  const requestedFilters = useRef(new Set());

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const companyOptions = useMemo(
    () => getActiveCompanyOptions(companies),
    [companies.data, companies.codesMap, companies.enabledData]
  );

  const isMultiCompany = companyOptions.length > 1;

  // Default the company selection to the sticky (last-used) company if it's
  // still available, otherwise the first company in the list.
  useEffect(() => {
    if (!isMultiCompany || companyOptions.length === 0) return;
    if (
      selectedCompanyUuid &&
      companyOptions.some((opt) => opt.value === selectedCompanyUuid)
    ) {
      return;
    }
    const stored = localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY);
    const initial =
      stored && companyOptions.some((opt) => opt.value === stored)
        ? stored
        : companyOptions[0].value;
    setSelectedCompanyUuid(initial);
  }, [isMultiCompany, companyOptions, selectedCompanyUuid]);

  const handleCompanyChange = (e) => {
    const uuid = e.target.value;
    setSelectedCompanyUuid(uuid);
    localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, uuid);
  };

  const filteredGroups = useMemo(() => {
    let data = accessGroups.data;

    if (isMultiCompany && selectedCompanyUuid) {
      data = data.filter((group) => group.company_uuid === selectedCompanyUuid);
    }

    if (!searchTerm) return data;

    const searchLower = searchTerm.toLowerCase();
    return data.filter((group) => {
      const allowedData = (group.access_codes || [])
        .map(getAccessCodeLabel)
        .join(", ")
        .toLowerCase();
      const companyName = (
        companies.data[group.company_uuid] || ""
      ).toLowerCase();
      return (
        group.name?.toLowerCase().includes(searchLower) ||
        allowedData.includes(searchLower) ||
        companyName.includes(searchLower)
      );
    });
  }, [
    accessGroups.data,
    searchTerm,
    isMultiCompany,
    selectedCompanyUuid,
    companies.data,
  ]);

  const sortedGroups = useMemo(() => {
    if (!sortConfig.key) return filteredGroups;

    return [...filteredGroups].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "access_codes") {
        aValue = (a.access_codes || []).join(", ");
        bValue = (b.access_codes || []).join(", ");
      } else if (sortConfig.key === "member_count") {
        aValue = parseInt(a.member_count || "0", 10);
        bValue = parseInt(b.member_count || "0", 10);
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      } else if (sortConfig.key === "group_type") {
        aValue = getGroupTypeLabel(a.group_type);
        bValue = getGroupTypeLabel(b.group_type);
      } else if (sortConfig.key === "company_uuid") {
        aValue = companies.data[a.company_uuid] || a.company_uuid || "";
        bValue = companies.data[b.company_uuid] || b.company_uuid || "";
      }

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });
  }, [filteredGroups, sortConfig]);

  const paginatedGroups = useMemo(() => {
    const startIndex =
      (paginationConfig.currentPage - 1) * paginationConfig.pageSize;
    return sortedGroups.slice(
      startIndex,
      startIndex + paginationConfig.pageSize
    );
  }, [sortedGroups, paginationConfig]);

  useEffect(() => {
    setPaginationConfig((prev) => ({ ...prev, currentPage: 1 }));
  }, [filteredGroups.length]);

  // Groups without access_codes show filter-mode tags instead, so load
  // their filters for the visible page (cached by group uuid).
  useEffect(() => {
    paginatedGroups.forEach((group) => {
      if (groupFilters[group.uuid] || requestedFilters.current.has(group.uuid))
        return;
      requestedFilters.current.add(group.uuid);
      FieldConfigurationAPI.getGroupFilters(group.uuid, group.company_uuid)
        .then((response) => {
          if (response.status === 200 && Array.isArray(response.data)) {
            setGroupFilters((prev) => ({
              ...prev,
              [group.uuid]: response.data,
            }));
          } else {
            requestedFilters.current.delete(group.uuid);
          }
        })
        .catch(() => requestedFilters.current.delete(group.uuid));
    });
  }, [paginatedGroups, groupFilters]);

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleDelete = async (group, event) => {
    event.stopPropagation();
    const memberCount = parseInt(group.member_count || "0", 10);
    if (memberCount > 0) {
      alert(
        `This Access Group can't be deleted because it's currently assigned to one or more users. Please reassign those users to a different access group before deleting this one.`
      );
      return;
    }
    if (
      !window.confirm(
        `Are you sure you want to delete this Access Group?\n\nThis is a permanent action, the access group cannot be recovered once deleted.`
      )
    ) {
      return;
    }
    const result = await deleteGroup(group.uuid, group.company_uuid);
    if (!result.success) {
      alert(
        `Failed to delete access group "${group.name}". Please try again or contact support if the issue persists.`
      );
    }
  };

  const handleCopyClick = (group, event) => {
    event.stopPropagation();
    setCopyGroup(group);
  };

  const handleCopied = () => {
    setCopyGroup(null);
    fetchGroups(true);
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      width: "200px",
      render: (group, value) => (
        <span
          style={{
            fontWeight: 500,
            color: "rgba(46, 50, 66, 1)",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "200px",
          }}
          title={value || ""}
        >
          {value}
        </span>
      ),
    },
    {
      key: "description",
      label: "Description",
      sortable: true,
      width: "250px",
      render: (group, value) => (
        <span
          style={{
            color: "rgba(100, 116, 139, 1)",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "250px",
          }}
          title={value || ""}
        >
          {value || "—"}
        </span>
      ),
    },
    {
      key: "access_codes",
      label: "Allowed Data",
      sortable: true,
      width: "500px",
      render: (group) => {
        const codes = group.access_codes || [];
        const loaded = groupFilters[group.uuid];
        const dash = <span style={{ color: "rgba(100, 116, 139, 1)" }}>—</span>;
        if (!loaded) return dash;
        // Filter rows are authoritative; legacy access_codes are the fallback.
        if (loaded.length > 0) {
          const enabled = getEnabledResources(
            getVisibleResources(group.group_type || "app"),
            (key) => loaded.find((f) => f.resource === key)
          );
          if (enabled.length === 0) return dash;
          return (
            <div className="access-code-tags">
              {enabled.map(({ resource, mode }) => (
                <span
                  key={resource.key}
                  className={`access-code-tag access-code-tag--${getFilterTagModifier(
                    mode
                  )}`}
                  title={`${resource.label}: ${mode}`}
                >
                  {resource.label}
                </span>
              ))}
            </div>
          );
        }
        if (codes.length === 0) return dash;
        const visible = codes.slice(0, 10);
        const overflow = codes.length - visible.length;
        return (
          <div className="access-code-tags">
            {visible.map((code) => (
              <span key={code} className="access-code-tag">
                {getAccessCodeLabel(code)}
              </span>
            ))}
            {overflow > 0 && (
              <span
                className="access-code-tag"
                title={codes.slice(10).map(getAccessCodeLabel).join(", ")}
              >
                +{overflow} more
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "member_count",
      label: "Users",
      sortable: true,
      width: "100px",
      render: (group) => (
        <span style={{ color: "rgba(100, 116, 139, 1)" }}>
          {parseInt(group.member_count || "0", 10)}
        </span>
      ),
    },
    {
      key: "group_type",
      label: "Type",
      sortable: true,
      width: "100px",
      render: (group) => (
        <span style={{ color: "rgba(100, 116, 139, 1)" }}>
          {getGroupTypeLabel(group.group_type)}
        </span>
      ),
    },
  ];

  const exportColumns = [
    { key: "name", label: "Name" },
    {
      key: "description",
      label: "Description",
      format: (val) => val || "",
    },
    {
      key: "access_codes",
      label: "Allowed Data",
      format: (val) => (val || []).map(getAccessCodeLabel).join(", "),
    },
    {
      key: "member_count",
      label: "Users",
      format: (val) => parseInt(val || "0", 10),
    },
    {
      key: "group_type",
      label: "Type",
      format: getGroupTypeLabel,
    },
  ];

  const actions = [
    {
      label: "Copy",
      icon: Copy,
      onClick: handleCopyClick,
    },
    {
      label: "Delete",
      icon: Trash2,
      onClick: handleDelete,
      className: "action-button-delete",
    },
  ];

  return (
    <div className="roles-list">
      <div className="roles-list__header">
        <div className="roles-list__header-top">
          <div className="roles-list__header-left">
            <h2 className="roles-list__title">Access Groups</h2>
            {isMultiCompany && (
              <div className="roles-list__company-filter">
                <Building2
                  size={18}
                  style={{ color: "rgba(100, 116, 139, 1)", flexShrink: 0 }}
                />
                <select
                  className="roles-list__company-select"
                  value={selectedCompanyUuid}
                  onChange={handleCompanyChange}
                  title={
                    companyOptions.find(
                      (opt) => opt.value === selectedCompanyUuid
                    )?.label || ""
                  }
                >
                  {companyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search access groups..."
          />
          <div className="roles-list__actions">
            <ExportButton
              data={sortedGroups}
              filename="access_groups_export"
              columns={exportColumns}
              className="button--secondary"
            />
            <Button
              className="button--secondary"
              onClick={() => fetchGroups(true)}
              disabled={accessGroups.loading}
              title="Refresh access groups"
            >
              <RefreshCw size={18} style={{ marginRight: "6px" }} />
              Refresh
            </Button>
            <Button className="button--primary" onClick={onAddGroup}>
              <Plus size={18} style={{ marginRight: "6px" }} />
              Add Access Group
            </Button>
          </div>
        </div>
      </div>

      {accessGroups.loading && (
        <div className="roles-list__loading">
          <p>Loading access groups...</p>
        </div>
      )}

      {accessGroups.error && (
        <div className="roles-list__error">
          <p>Error loading access groups: {accessGroups.error}</p>
          <Button onClick={() => fetchGroups(true)}>Retry</Button>
        </div>
      )}

      {!accessGroups.loading && !accessGroups.error && (
        <>
          <SortableTable
            data={paginatedGroups}
            columns={columns}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={onGroupSelect}
            actions={actions}
            emptyMessage="No access groups found. Click 'Add Access Group' to create your first group."
          />
          <PaginationControls
            currentPage={paginationConfig.currentPage}
            pageSize={paginationConfig.pageSize}
            totalItems={sortedGroups.length}
            onPageChange={(newPage) => {
              setPaginationConfig((prev) => ({
                ...prev,
                currentPage: newPage,
              }));
            }}
          />
        </>
      )}

      <CopyGroupModal
        open={!!copyGroup}
        group={copyGroup}
        companies={companies}
        onClose={() => setCopyGroup(null)}
        onCopied={handleCopied}
      />
    </div>
  );
}
